// Shared price lookups: every card on a page asks for its price, and the
// requests are batched into one /prices/ call per tick.
import { useEffect, useState } from 'react';
import { getConfig } from '@edx/frontend-platform';
import { getAuthenticatedUser } from '@edx/frontend-platform/auth';

import { addToCart, addToWishlist, getCart, getPrices, removeFromWishlist } from './api';

const cache = new Map(); // "course:<id>" -> price info
const waiting = new Map(); // key -> [resolve]
let timer = null;

function flush() {
  timer = null;
  const keys = [...waiting.keys()];
  const pending = new Map(waiting);
  waiting.clear();
  for (let i = 0; i < keys.length; i += 50) {
    const chunk = keys.slice(i, i + 50);
    const pick = kind => chunk.filter(k => k.startsWith(`${kind}:`)).map(k => k.slice(kind.length + 1));
    getPrices({ courses: pick('course') })
      .then((data) => {
        chunk.forEach((k) => {
          const info = data.prices[k] || { paid: false, price: 0 };
          info.payments = data.payments;
          cache.set(k, info);
          (pending.get(k) || []).forEach(resolve => resolve(info));
        });
      })
      .catch(() => chunk.forEach(k => (pending.get(k) || []).forEach(resolve => resolve(null))));
  }
}

export function loadPrice(kind, ref) {
  const key = `${kind}:${ref}`;
  if (cache.has(key)) { return Promise.resolve(cache.get(key)); }
  return new Promise((resolve) => {
    waiting.set(key, [...(waiting.get(key) || []), resolve]);
    if (!timer) { timer = setTimeout(flush, 30); }
  });
}

const forget = () => cache.clear();

// [price, reload] for one course; refreshed when the cart changes.
export function usePrice(kind, ref) {
  const key = `${kind}:${ref}`;
  const [price, setPrice] = useState(cache.get(key) || null);
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (!ref) { return undefined; }
    let live = true;
    loadPrice(kind, ref).then(p => live && setPrice(p));
    return () => { live = false; };
  }, [key, tick]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const on = () => { forget(); setTick(n => n + 1); };
    window.addEventListener('bm-cart-changed', on);
    return () => window.removeEventListener('bm-cart-changed', on);
  }, []);
  return [price, () => { forget(); setTick(n => n + 1); }];
}

export function useCartCount() {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!getAuthenticatedUser()) { return undefined; }
    let live = true;
    const load = () => getCart().then(c => live && setCount(c.lines.length + c.skipped.length)).catch(() => {});
    load();
    window.addEventListener('bm-cart-changed', load);
    return () => { live = false; window.removeEventListener('bm-cart-changed', load); };
  }, []);
  return count;
}

export function signInUrl(next) {
  return `${getConfig().LMS_BASE_URL}/login?next=${encodeURIComponent(next || window.location.href)}`;
}

// Wishlist toggle and add-to-cart for a course; guests sign in first.
export function useShopActions(kind, ref, price, reload) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const guard = (fn) => async (...args) => {
    if (!getAuthenticatedUser()) { window.location.href = signInUrl(); return false; }
    setBusy(true);
    setError('');
    try {
      await fn(...args);
      return true;
    } catch (e) {
      setError(e.code || 'error');
      return false;
    } finally {
      setBusy(false);
      reload();
    }
  };
  return {
    busy,
    error,
    toggleWish: guard(() => (price?.wished ? removeFromWishlist(kind, ref) : addToWishlist(kind, ref))),
    addCart: guard(() => (price?.in_cart ? Promise.resolve() : addToCart(kind, ref))),
  };
}
